<script setup lang="ts">
import {ref} from "vue";
import MedicationCheckupCopiloteLine from "@/medication-checkup/components/MedicationCheckupCopiloteLine.vue";
import MedicationCheckupUserEcho from "@/medication-checkup/components/MedicationCheckupUserEcho.vue";
import MedicationCheckupPatientProfileCard from "@/medication-checkup/components/MedicationCheckupPatientProfileCard.vue";
import MedicationCheckupCopiloteBubble from "@/medication-checkup/components/MedicationCheckupCopiloteBubble.vue";
import MedicationCheckupSceneReplies from "@/medication-checkup/components/MedicationCheckupSceneReplies.vue";
import {MedicationCheckupAction} from "@/medication-checkup/interfaces";
import {PatientProfile, PatientProfileView} from "@/medication-checkup/interfaces/patient-profile";
import {SceneProps, useScene} from "@/medication-checkup/composables/useScene";

/**
 * The scene of the patient profile block: what the analysis of the
 * interactions reads beside the treatments, verified with the patient right
 * after the treatments are retained.
 *
 * Everything it shows is served by the handler: the transition line, the
 * patient, the profile pre-filled from the record and how the pregnancy
 * section is asked. Entering values is the scene's own state, as checking
 * lines is in the collection: the closing action submits the profile whole.
 */

const props = defineProps<SceneProps<PatientProfileView>>();

const {bubbleShown, cardShown, clicked, view, text, actions, acted, act: submit} = useScene(
    props,
    {card: true}
);

/**
 * The profile as entered so far — the served pre-fill to begin with, local
 * until the closing action submits it. Resets with the scene. Copied field
 * by field: the served view is a reactive proxy, which no clone can walk.
 */
const draft = ref<PatientProfile | null>(view.value ? {
    ...view.value.profile,
    allergies: [...view.value.profile.allergies],
    pathologies: [...view.value.profile.pathologies],
} : null);

/**
 * Take the closing action, submitting the profile whole.
 */
const act = (action: MedicationCheckupAction) => submit(action, {...draft.value});
</script>

<template>
  <div class="flex flex-col gap-3.5">
    <template v-if="frozen">
      <MedicationCheckupCopiloteLine v-if="text" :text="text" />
      <MedicationCheckupPatientProfileCard v-if="view" :view="view" frozen />
      <MedicationCheckupUserEcho v-if="acted" :text="acted" />
    </template>

    <template v-else>
      <MedicationCheckupCopiloteBubble
        :shown="bubbleShown"
        :text="text"
      />

      <MedicationCheckupPatientProfileCard
        v-if="cardShown && view && draft"
        :view="view"
        :draft="draft"
        class="animate-message-in"
        @update:draft="draft = $event"
      />

      <MedicationCheckupSceneReplies
        :clicked="clicked"
        :actions="actions"
        :shown="cardShown"
        @act="act"
      />
    </template>
  </div>
</template>
